import type { AnchorHTMLAttributes } from 'react';
import { trackCtaClick, type CtaPlacement } from '../../utils/ctaTracking';
import type { TrackingContext } from '../types';
import { cn } from '../utils';

type Variant = 'light' | 'dark' | 'ghost';
type Size = 'sm' | 'md' | 'lg';

type LandingCtaButtonProps = Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'href'> & {
  href?: string;
  label: string;
  placement: CtaPlacement;
  tracking: TrackingContext;
  variant?: Variant;
  size?: Size;
};

const variantClasses: Record<Variant, string> = {
  light:
    'bg-white text-[#1C1C1E] shadow-[0_10px_24px_rgba(17,19,26,0.08)] hover:bg-[#F9FBFF] hover:shadow-[0_16px_36px_rgba(17,19,26,0.12)]',
  dark:
    'bg-[#101218] text-white shadow-[0_14px_32px_rgba(7,10,18,0.18)] hover:bg-[#171a24] hover:shadow-[0_20px_40px_rgba(7,10,18,0.22)]',
  ghost:
    'border border-white/20 bg-white/10 text-white shadow-[0_8px_18px_rgba(7,10,18,0.08)] hover:bg-white/16 hover:border-white/28 hover:shadow-[0_14px_28px_rgba(7,10,18,0.12)]',
};

const sizeClasses: Record<Size, string> = {
  sm: 'gap-3 px-4 py-2 text-[14px] leading-[20px]',
  md: 'gap-4 px-6 py-3 text-[16px] leading-[24px]',
  lg: 'gap-4 px-7 py-4 text-[16px] leading-[24px]',
};

export function LandingCtaButton({
  href,
  label,
  placement,
  tracking,
  variant = 'light',
  size = 'md',
  className,
  onClick,
  target = '_blank',
  rel = 'noopener noreferrer',
  ...rest
}: LandingCtaButtonProps) {
  const destination = href || tracking.registerUrl;

  return (
    <a
      {...rest}
      href={destination}
      target={target}
      rel={rel}
      onClick={(event) => {
        trackCtaClick({
          placement,
          language: tracking.language,
          destination,
          hasAnalyticsConsent: tracking.hasAnalyticsConsent,
        });

        onClick?.(event);
      }}
      className={cn(
        'landing-cta-interactive inline-flex items-center justify-center rounded-full font-pangram font-medium',
        variantClasses[variant],
        sizeClasses[size],
        className
      )}
    >
      <span>{label}</span>
      <span
        className={cn(
          'landing-cta-dot inline-flex items-center justify-center rounded-full',
          variant === 'dark' ? 'bg-white' : 'bg-[#6195FF]',
          size === 'sm' ? 'h-2 w-2' : 'h-3 w-3'
        )}
        aria-hidden="true"
      />
    </a>
  );
}
